/**
 * Interface for battery information
 */
interface BatteryInfo {
    charging: boolean;
    level: number;
    chargingTime: number;
    dischargingTime: number;
}

/**
 * Interface for media device information
 */
interface MediaDeviceInfo {
    kind: string;
    label: string;
    deviceId: string;
}

/**
 * Interface for IP data returned from ipify.org
 */
interface IpifyResponse {
    ip: string;
}

/**
 * Interface for the system information returned by getSystemInfo
 */
interface SystemInfo {
    [key: string]: any;
    oscpu?: string;
    hardwareConcurrency?: number;
    deviceMemory?: any;
    platform?: string;
    userAgent: string;
    product?: string;
    productSub?: string;
    appCodeName?: string;
    appName?: string;
    appVersion?: string;
    vendor?: string;
    vendorSub?: string;
    language: string;
    connection?: any;
    diskQuota?: number | "unknown";
    battery?: BatteryInfo | "unknown";
    mediaDevices?: MediaDeviceInfo[] | "unknown";
    ip?: string;
}

/**
 * Gets detailed system information from the browser
 * @returns A promise that resolves to system information object
 */
export async function getSystemInfo(): Promise<SystemInfo> {
    const info: SystemInfo = {
        userAgent: navigator.userAgent,
        language: navigator.language
    };

    // Standard and non-standard navigator properties
    info.oscpu = (navigator as any).oscpu;
    info.hardwareConcurrency = navigator.hardwareConcurrency;
    info.deviceMemory = (navigator as any).deviceMemory;
    info.platform = navigator.platform;
    info.product = navigator.product;
    info.productSub = navigator.productSub;
    info.appCodeName = navigator.appCodeName;
    info.appName = navigator.appName;
    info.appVersion = navigator.appVersion;
    info.vendor = navigator.vendor;
    info.vendorSub = navigator.vendorSub;

    // Network information
    if ((navigator as any).connection) {
        info.connection = (navigator as any).connection;
    }

    // Storage information
    try {
        const disk = await navigator.storage.estimate();
        info.diskQuota = disk.quota;
    } catch (e) {
        info.diskQuota = "unknown";
    }

    // Battery information
    try {
        const battery = await (navigator as any).getBattery();
        info.battery = {
            charging: battery.charging,
            level: battery.level,
            chargingTime: battery.chargingTime,
            dischargingTime: battery.dischargingTime
        };
    } catch (e) {
        info.battery = "unknown";
    }

    // Media devices
    try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        info.mediaDevices = devices.map(device => ({
            kind: device.kind,
            label: device.label,
            deviceId: device.deviceId
        }));
    } catch (e) {
        info.mediaDevices = "unknown";
    }

    // IP address
    try {
        const res = await fetch("https://api.ipify.org?format=json");
        const ipData: IpifyResponse = await res.json();
        info.ip = ipData.ip;
    } catch (e) {
        info.ip = "unknown";
    }

    return info;
}
